import { useEffect, useRef } from 'react';
import { TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.vectorgrid';
import { getBackendUrl } from '../../config/api';
import { VECTOR_CHARTS_LAYER_STYLES } from './vectorChartsStyles';
import NavionicsChartLayer from './NavionicsChartLayer';
import { getMarineChartRegion, getTswChartMode } from './marineChartRegion';

export const CHART_PREFS_VERSION = '10';
export const CHART_MIN_ZOOM = 8;
export const CHART_MAX_ZOOM = 19;

/** CHS NONNA WMS layer (10 m cells; non-navigational bathymetry). */
export const CHS_NONNA_WMS_LAYER = 'nonna:NONNA 10';

export function ensureChartPrefsDefaults() {
  if (localStorage.getItem('chart_prefs_version') === CHART_PREFS_VERSION) return;
  localStorage.setItem('show_chs_bathymetry', 'true');
  localStorage.setItem('show_gebco', 'false');
  localStorage.setItem('show_depth_layers', 'true');
  localStorage.setItem('use_vector_charts', 'false');
  localStorage.setItem('follow_vessel', 'true');
  localStorage.setItem('show_marine_pois', 'true');
  localStorage.setItem('map_base_layer', 'marine');
  localStorage.setItem('chart_prefs_version', CHART_PREFS_VERSION);
}

/** Simple street map — OSM single-host tiles (reliable in production). */
export const STREET_MAP_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
export const STREET_MAP_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
export const STREET_MAP_SUBDOMAINS = undefined;
export const SATELLITE_MAP_URL =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
export const SATELLITE_MAP_ATTRIBUTION =
  '&copy; <a href="https://www.esri.com">Esri</a>';

export function BasicMapLayer() {
  return (
    <TileLayer
      attribution={STREET_MAP_ATTRIBUTION}
      url={STREET_MAP_URL}
      subdomains={STREET_MAP_SUBDOMAINS}
      maxZoom={CHART_MAX_ZOOM}
      minZoom={2}
    />
  );
}

/** Satellite imagery overlay option. */
export function SatelliteMapLayer() {
  return (
    <TileLayer
      attribution={SATELLITE_MAP_ATTRIBUTION}
      url={SATELLITE_MAP_URL}
      maxZoom={19}
      minZoom={2}
    />
  );
}

/** Fix blank map when container size isn't known at first paint. */
export function MapInvalidateSize() {
  const map = useMap();
  useEffect(() => {
    const fix = () => map.invalidateSize();
    fix();
    const t1 = setTimeout(fix, 100);
    const t2 = setTimeout(fix, 500);
    window.addEventListener('resize', fix);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('resize', fix);
    };
  }, [map]);
  return null;
}

function apiBase() {
  return getBackendUrl().replace(/\/$/, '');
}

/** Probe all chart backends configured on the server. */
export async function probeChartServices() {
  const base = apiBase();
  try {
    const statusResp = await fetch(`${base}/api/charts/status`);
    if (statusResp.ok) {
      return await statusResp.json();
    }
  } catch (_) {
    /* fall through */
  }
  return {
    vectorCharts: await probeVectorChartsAvailable(),
    cmap: await probeCmapAvailable(),
    chsEnc: true,
  };
}

/** True when server (or per-user) C-MAP credentials can load chart tiles. */
export async function probeCmapAvailable() {
  try {
    const base = apiBase();
    const statusResp = await fetch(`${base}/api/charts/status`);
    if (statusResp.ok) {
      const data = await statusResp.json();
      if (data?.cmap) return true;
    }
  } catch (_) {
    /* fall through to tile probe */
  }

  try {
    // Peterborough / Trent-Severn area
    const tileResp = await fetch(`${apiBase()}/api/cmap/tiles/14/4852/5968.png`);
    if (!tileResp.ok) return false;
    const ct = tileResp.headers.get('content-type') || '';
    return ct.includes('image');
  } catch (_) {
    return false;
  }
}

export function cmapTileUrl(clientId, clientSecret) {
  const base = `${apiBase()}/api/cmap/tiles/{z}/{y}/{x}.png`;
  if (clientId && clientSecret) {
    return `${base}?client_id=${encodeURIComponent(clientId)}&client_secret=${encodeURIComponent(clientSecret)}`;
  }
  return base;
}

/** C-MAP Professional+ — covers Trent-Severn and Canadian inland waterways. */
export function CmapChartLayer({ clientId, clientSecret } = {}) {
  return (
    <TileLayer
      attribution='&copy; <a href="https://www.c-map.com">C-MAP</a>'
      url={cmapTileUrl(clientId, clientSecret)}
      maxZoom={CHART_MAX_ZOOM}
      minZoom={CHART_MIN_ZOOM}
      zIndex={100}
    />
  );
}

/** @deprecated use probeChartServices */
export async function probeVectorChartsAvailable() {
  try {
    const base = apiBase();
    const statusResp = await fetch(`${base}/api/charts/status`);
    if (statusResp.ok) {
      const data = await statusResp.json();
      if (data?.vectorCharts) return true;
    }
  } catch (_) {
    /* fall through to tile probe */
  }

  try {
    const tileResp = await fetch(`${apiBase()}/api/vectorcharts/tiles/10/301/384.mvt`);
    if (!tileResp.ok) return false;
    const ct = tileResp.headers.get('content-type') || '';
    return ct.includes('protobuf') || ct.includes('octet-stream');
  } catch (_) {
    return false;
  }
}

/**
 * VectorCharts enc-v2 — US waters only (no Canadian ENC coverage).
 */
export function VectorChartsLayer() {
  const map = useMap();
  const layerRef = useRef(null);

  useEffect(() => {
    if (!map || !L.vectorGrid?.protobuf) return;

    const tileUrl = `${apiBase()}/api/vectorcharts/tiles/{z}/{x}/{y}.mvt`;
    const vectorLayer = L.vectorGrid.protobuf(tileUrl, {
      rendererFactory: L.canvas.tile,
      vectorTileLayerStyles: VECTOR_CHARTS_LAYER_STYLES,
      interactive: false,
      maxNativeZoom: 18,
      minZoom: CHART_MIN_ZOOM,
      maxZoom: CHART_MAX_ZOOM,
      attribution: '&copy; <a href="https://vectorcharts.com">VectorCharts</a>',
    });

    vectorLayer.addTo(map);
    layerRef.current = vectorLayer;

    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map]);

  return null;
}

/** Ensure map allows harbor-level zoom. */
export function MapZoomLimits() {
  const map = useMap();
  useEffect(() => {
    map.setMinZoom(CHART_MIN_ZOOM);
    map.setMaxZoom(CHART_MAX_ZOOM);
  }, [map]);
  return null;
}

/** Minimum PNG size from CHS export that indicates real chart content. */
const ENC_MIN_BYTES = 4000;

/**
 * CHS ENC via MapServer dynamic export – renders a full nautical chart for the
 * current viewport at any zoom (no WMS tile index boxes).
 * Skips display when CHS returns an empty tile (common on inland waterways).
 */
export function DynamicCanadianEncLayer({ enabled = true, onCoverageChange }) {
  const map = useMap();
  const overlayRef = useRef(null);
  const timerRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!map || !enabled) return;

    const refresh = () => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        const bounds = map.getBounds();
        const size = map.getSize();
        if (!size.x || !size.y) return;

        const zoom = map.getZoom();
        const sw = map.project(bounds.getSouthWest(), zoom);
        const ne = map.project(bounds.getNorthEast(), zoom);
        const bbox = `${sw.x},${sw.y},${ne.x},${ne.y}`;
        const reqId = ++requestIdRef.current;
        const url =
          `${apiBase()}/api/enc-export?bbox=${encodeURIComponent(bbox)}` +
          `&width=${Math.round(size.x)}&height=${Math.round(size.y)}&_=${Date.now()}`;

        fetch(url)
          .then(async (resp) => {
            if (reqId !== requestIdRef.current) return null;
            if (!resp.ok) {
              onCoverageChange?.(false);
              return null;
            }
            const blob = await resp.blob();
            const coverageHeader = resp.headers.get('X-Enc-Coverage');
            const covered = coverageHeader
              ? coverageHeader !== 'none'
              : blob.size >= ENC_MIN_BYTES;
            onCoverageChange?.(covered);
            if (!covered) return null;
            return blob;
          })
          .then((blob) => {
            if (!blob || reqId !== requestIdRef.current) return;

            if (overlayRef.current) {
              map.removeLayer(overlayRef.current);
              overlayRef.current = null;
            }

            const objectUrl = URL.createObjectURL(blob);
            const img = L.imageOverlay(objectUrl, bounds, {
              opacity: 1,
              zIndex: 150,
              interactive: false,
              className: 'boatwise-enc-chart-layer',
            });
            img.addTo(map);

            const el = img.getElement?.();
            if (el) {
              el.style.transition = 'opacity 0.15s ease';
              el.style.opacity = '0.4';
            }

            img.once('load', () => {
              if (reqId !== requestIdRef.current) return;
              if (el) el.style.opacity = '1';
            });

            img.once('remove', () => URL.revokeObjectURL(objectUrl));
            overlayRef.current = img;
          })
          .catch(() => onCoverageChange?.(false));
      }, 200);
    };

    map.on('moveend zoomend resize', refresh);
    refresh();

    return () => {
      clearTimeout(timerRef.current);
      map.off('moveend zoomend resize', refresh);
      if (overlayRef.current) {
        map.removeLayer(overlayRef.current);
        overlayRef.current = null;
      }
    };
  }, [map, enabled, onCoverageChange]);

  return null;
}

/** OpenSeaMap seamarks (buoys, lights, etc.). */
export function OpenSeaMapSeamarkLayer() {
  return (
    <TileLayer
      attribution='&copy; <a href="https://www.openseamap.org">OpenSeaMap</a>'
      url="https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png"
      maxZoom={18}
      minZoom={CHART_MIN_ZOOM}
      zIndex={120}
      opacity={1}
    />
  );
}

/** OpenSeaMap depth contours (global; sparse in some areas). */
export function OpenSeaMapDepthLayer() {
  return (
    <TileLayer
      attribution='&copy; <a href="https://www.openseamap.org">OpenSeaMap</a> depth'
      url="https://tiles.openseamap.org/depth/{z}/{x}/{y}.png"
      maxZoom={18}
      minZoom={CHART_MIN_ZOOM}
      zIndex={115}
      opacity={0.85}
    />
  );
}

/** CHS NONNA 10 m bathymetry via backend WMS proxy (Canadian waters). */
export function ChsBathymetryLayer({ enabled = true } = {}) {
  const map = useMap();
  const layerRef = useRef(null);

  useEffect(() => {
    if (!map || !enabled) return;

    const layer = L.tileLayer.wms(`${apiBase()}/api/chs-bathymetry-wms`, {
      layers: CHS_NONNA_WMS_LAYER,
      format: 'image/png',
      transparent: true,
      version: '1.3.0',
      opacity: 0.7,
      attribution:
        '&copy; <a href="https://open.canada.ca/data/en/dataset/d3881c4c-650d-4070-bf9b-1e00aabf0a1d">CHS NONNA</a> (non-navigational)',
    });
    layer.setZIndex(110);
    layer.addTo(map);
    layerRef.current = layer;

    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map, enabled]);

  return null;
}

/**
 * Depth contour overlays — CHS NONNA for Canada, OpenSeaMap depth globally.
 */
export function MarineDepthOverlays({
  latitude,
  longitude,
  showChsBathymetry = true,
  showOpenSeaMapDepth = true,
} = {}) {
  const region = getMarineChartRegion(latitude, longitude);
  const isCanada = region === 'canada' || region === 'canada_inland';

  return (
    <>
      {showOpenSeaMapDepth && <OpenSeaMapDepthLayer />}
      {showChsBathymetry && isCanada && <ChsBathymetryLayer enabled />}
    </>
  );
}

/**
 * Best available free chart stack for Trent-Severn and other inland waterways
 * where CHS web ENC has no coverage (canals, Kawartha lakes, Lake Simcoe, etc.).
 */
export function InlandWaterwayChartLayer() {
  return (
    <>
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>'
        url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
        maxZoom={CHART_MAX_ZOOM}
        minZoom={CHART_MIN_ZOOM}
        zIndex={0}
      />
      <OpenSeaMapSeamarkLayer />
    </>
  );
}

/** Primary marine chart stack — C-MAP on inland waterways, CHS ENC elsewhere. */
export function CanadianMarineChartLayer({
  latitude,
  longitude,
  cmapAvailable = false,
  cmapClientId,
  cmapClientSecret,
} = {}) {
  const region = getMarineChartRegion(latitude, longitude);
  const tswMode = getTswChartMode(latitude, longitude);

  if (region === 'canada_inland') {
    if (cmapAvailable) {
      return (
        <>
          <CmapChartLayer clientId={cmapClientId} clientSecret={cmapClientSecret} />
          <OpenSeaMapSeamarkLayer />
        </>
      );
    }
    return <InlandWaterwayChartLayer />;
  }

  return (
    <>
      <TileLayer
        attribution='&copy; <a href="https://www.charts.gc.ca">Canadian Hydrographic Service</a>'
        url="https://services.arcgisonline.com/arcgis/rest/services/Ocean/World_Ocean_Base/MapServer/tile/{z}/{y}/{x}"
        maxZoom={CHART_MAX_ZOOM}
        minZoom={CHART_MIN_ZOOM}
        zIndex={0}
        opacity={tswMode ? 0.7 : 0.85}
      />
      <DynamicCanadianEncLayer />
      <OpenSeaMapSeamarkLayer />
    </>
  );
}

/** Official CHS dynamic ENC — use for all Canadian waters including Great Lakes. */
export function CanadianEncLayer({
  latitude,
  longitude,
  cmapAvailable = false,
  cmapClientId,
  cmapClientSecret,
} = {}) {
  return (
    <CanadianMarineChartLayer
      latitude={latitude}
      longitude={longitude}
      cmapAvailable={cmapAvailable}
      cmapClientId={cmapClientId}
      cmapClientSecret={cmapClientSecret}
    />
  );
}

/** US marine charts: VectorCharts when enabled, otherwise NOAA raster. */
export function USMarineChartLayer({ useVectorCharts = false } = {}) {
  if (useVectorCharts) return <VectorChartsLayer />;
  return <NOAAChartLayer />;
}

/**
 * Primary integrated chart layer — Navionics when key is configured, else regional fallback.
 */
export function IntegratedMarineChartLayer({
  latitude,
  longitude,
  navionicsKey,
  navionicsChartType = 'nautical',
  useVectorCharts = false,
  vectorChartsAvailable = false,
  cmapAvailable = false,
  cmapClientId,
  cmapClientSecret,
} = {}) {
  if (navionicsKey) {
    return <NavionicsChartLayer navKey={navionicsKey} chartType={navionicsChartType} />;
  }

  return (
    <AdaptiveMarineChartLayer
      latitude={latitude}
      longitude={longitude}
      useVectorCharts={useVectorCharts}
      vectorChartsAvailable={vectorChartsAvailable}
      cmapAvailable={cmapAvailable}
      cmapClientId={cmapClientId}
      cmapClientSecret={cmapClientSecret}
    />
  );
}

/**
 * Auto-select chart source from vessel position when Navionics is not configured.
 */
export function AdaptiveMarineChartLayer({
  latitude,
  longitude,
  useVectorCharts = false,
  vectorChartsAvailable = false,
  cmapAvailable = false,
  cmapClientId,
  cmapClientSecret,
} = {}) {
  const region = getMarineChartRegion(latitude, longitude);

  if (region === 'us' && useVectorCharts && vectorChartsAvailable) {
    return <VectorChartsLayer />;
  }
  if (region === 'us') {
    return <NOAAChartLayer />;
  }
  return (
    <CanadianMarineChartLayer
      latitude={latitude}
      longitude={longitude}
      cmapAvailable={cmapAvailable}
      cmapClientId={cmapClientId}
      cmapClientSecret={cmapClientSecret}
    />
  );
}

/** NOAA ENC raster charts (US waters). */
export function NOAAChartLayer() {
  return (
    <>
      <TileLayer
        attribution='&copy; <a href="https://www.esri.com">Esri</a>'
        url="https://services.arcgisonline.com/arcgis/rest/services/Ocean/World_Ocean_Base/MapServer/tile/{z}/{y}/{x}"
        maxZoom={CHART_MAX_ZOOM}
        minZoom={CHART_MIN_ZOOM}
        zIndex={0}
      />
      <TileLayer
        attribution='&copy; <a href="https://nauticalcharts.noaa.gov">NOAA</a>'
        url={`${apiBase()}/api/noaa-charts/{z}/{y}/{x}.png`}
        maxZoom={CHART_MAX_ZOOM}
        minZoom={4}
        zIndex={100}
      />
    </>
  );
}

function distanceMeters(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(Δφ / 2) ** 2 +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function normalizeMapPosition(position) {
  if (!position || position.length !== 2) return null;
  const lat = typeof position[0] === 'number' ? position[0] : parseFloat(position[0]);
  const lng = typeof position[1] === 'number' ? position[1] : parseFloat(position[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return [lat, lng];
}

function flyMapTo(map, lat, lng, zoomLevel, duration = 0.75) {
  map.stop();
  map._boatwiseProgrammaticMove = (map._boatwiseProgrammaticMove || 0) + 1;
  map.flyTo([lat, lng], zoomLevel, { duration });
  map.once('moveend', () => {
    map._boatwiseProgrammaticMove = Math.max(0, (map._boatwiseProgrammaticMove || 1) - 1);
  });
}

function panMapTo(map, lat, lng) {
  map.stop();
  map._boatwiseProgrammaticMove = (map._boatwiseProgrammaticMove || 0) + 1;
  map.panTo([lat, lng], { animate: false, duration: 0 });
  map.once('moveend', () => {
    map._boatwiseProgrammaticMove = Math.max(0, (map._boatwiseProgrammaticMove || 1) - 1);
  });
}

const CHART_VIEWPORT_KEY = 'chart_viewport';

/** Restore last map center/zoom for this browser tab (survives navigation away). */
export function loadChartViewport() {
  try {
    const raw = sessionStorage.getItem(CHART_VIEWPORT_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (Number.isFinite(v.lat) && Number.isFinite(v.lng) && Number.isFinite(v.zoom)) {
      return { center: [v.lat, v.lng], zoom: v.zoom };
    }
  } catch {
    /* ignore */
  }
  return null;
}

/** Persist map center/zoom on pan/zoom so returning to Navigation restores the view. */
export function ChartViewportPersistence() {
  const map = useMap();
  useEffect(() => {
    const save = () => {
      const c = map.getCenter();
      sessionStorage.setItem(
        CHART_VIEWPORT_KEY,
        JSON.stringify({ lat: c.lat, lng: c.lng, zoom: map.getZoom() })
      );
    };
    map.on('moveend', save);
    map.on('zoomend', save);
    return () => {
      map.off('moveend', save);
      map.off('zoomend', save);
    };
  }, [map]);
  return null;
}

/** Center on vessel at first GPS fix; optional follow pans without changing zoom. */
export function VesselMapTracker({
  position,
  zoom = 14,
  follow = true,
  recenterToken = 0,
  forceCenter = false,
  speedKnots = 0,
  skipInitialCenter = false,
}) {
  const map = useMap();
  const hasCenteredRef = useRef(skipInitialCenter);
  const followRef = useRef(follow);
  const prevFollowRef = useRef(follow);
  const lastLatRef = useRef(null);
  const lastLngRef = useRef(null);
  const lastFollowPanAtRef = useRef(0);
  const panInFlightRef = useRef(false);
  const speedRef = useRef(speedKnots);
  const lastRecenterTokenRef = useRef(0);

  useEffect(() => {
    speedRef.current = speedKnots;
  }, [speedKnots]);

  useEffect(() => {
    followRef.current = follow;
  }, [follow]);

  useEffect(() => {
    if (forceCenter) hasCenteredRef.current = false;
  }, [forceCenter]);

  // Recenter button only — must NOT re-run on every GPS update (that locked zoom at 14).
  useEffect(() => {
    if (!recenterToken || recenterToken === lastRecenterTokenRef.current) return;
    const coords = normalizeMapPosition(position);
    if (!coords) return;

    const [lat, lng] = coords;
    lastRecenterTokenRef.current = recenterToken;
    const targetZoom = map.getZoom() > 0 ? map.getZoom() : zoom;
    flyMapTo(map, lat, lng, targetZoom, 0.5);
    hasCenteredRef.current = true;
    lastLatRef.current = lat;
    lastLngRef.current = lng;
    lastFollowPanAtRef.current = Date.now();
    panInFlightRef.current = false;
  }, [recenterToken, position?.[0], position?.[1], map, zoom]);

  useEffect(() => {
    const coords = normalizeMapPosition(position);
    if (!coords) return;
    const [lat, lng] = coords;

    const prevLat = lastLatRef.current;
    const prevLng = lastLngRef.current;
    const followJustEnabled = follow && !prevFollowRef.current;
    prevFollowRef.current = follow;

    const sameFix =
      prevLat != null &&
      prevLng != null &&
      Math.abs(prevLat - lat) < 1e-5 &&
      Math.abs(prevLng - lng) < 1e-5;

    // First GPS fix: center once unless user returned to a saved viewport.
    if (!hasCenteredRef.current) {
      const initialZoom = map.getZoom() > 0 ? map.getZoom() : zoom;
      panMapTo(map, lat, lng);
      if (map.getZoom() < 1) {
        map.setZoom(initialZoom, { animate: false });
      }
      hasCenteredRef.current = true;
      lastLatRef.current = lat;
      lastLngRef.current = lng;
      return;
    }

    // Parent re-renders (heading, AIS, etc.) pass a fresh [lat,lng] array
    // with identical coordinates — skip follow logic entirely.
    if (sameFix && !followJustEnabled) return;

    lastLatRef.current = lat;
    lastLngRef.current = lng;

    if (!followRef.current) return;

    // User turned follow back on (e.g. recenter after searching a place) — always
    // snap to the boat, even at the dock. Without this, the speed gate below
    // left the map stuck on the searched point after recenter.
    if (followJustEnabled) {
      panMapTo(map, lat, lng);
      panInFlightRef.current = false;
      lastFollowPanAtRef.current = Date.now();
      return;
    }

    const speed = Number.isFinite(speedRef.current) ? speedRef.current : 0;

    // At the dock, keep the map still — only the vessel icon moves.
    if (speed < 1.0) return;

    // When stationary, ignore small GPS jitter entirely.
    if (!followJustEnabled && prevLat != null && prevLng != null) {
      const minMoveM = speed < 2 ? 4 : 1.5;
      if (distanceMeters(prevLat, prevLng, lat, lng) < minMoveM) return;
    }

    if (panInFlightRef.current) return;

    const now = Date.now();
    const minPanIntervalMs = followJustEnabled ? 0 : speed < 2 ? 800 : 300;
    if (now - lastFollowPanAtRef.current < minPanIntervalMs) return;

    const center = map.getCenter();
    const centerPt = map.latLngToContainerPoint(center);
    const vesselPt = map.latLngToContainerPoint([lat, lng]);
    const dx = vesselPt.x - centerPt.x;
    const dy = vesselPt.y - centerPt.y;
    const distPx = Math.sqrt(dx * dx + dy * dy);
    const thresholdPx = followJustEnabled ? 20 : speed < 2 ? 60 : 40;

    if (distPx <= thresholdPx) return;

    lastFollowPanAtRef.current = now;
    panInFlightRef.current = true;
    map._boatwiseProgrammaticMove = (map._boatwiseProgrammaticMove || 0) + 1;
    map.once('moveend', () => {
      panInFlightRef.current = false;
      map._boatwiseProgrammaticMove = Math.max(0, (map._boatwiseProgrammaticMove || 1) - 1);
    });

    // Follow = pan only. Never setView/zoom here — user controls zoom level.
    map.panTo([lat, lng], { animate: false, duration: 0 });
  }, [position?.[0], position?.[1], map, follow]);

  return null;
}
